import { mobileScreenWidth } from "./constants";

document.addEventListener("DOMContentLoaded", afterDOMContentLoaded);

// speed at the "1x" breakpoint, before the per-breakpoint and overall factors below are applied
const baseAutoScrollSpeed = 40; // px / second
const autoScrollSpeedFactor = 4 / 3; // 2x the previous 2/3 speed

let galleryTopRow: HTMLElement;
let galleryBottomRow: HTMLElement;
let topLoopStart: HTMLElement;
let bottomLoopStart: HTMLElement;

let isGalleryTopVisible = false;
let isGalleryBottomVisible = false;

let topSpeedPxPerSec = 0;
let bottomSpeedPxPerSec = 0;
let topOffset = 0;
let bottomOffset = 0;
let topWrapDistance = 0;
let bottomWrapDistance = 0;

let lastFrameTime: number | null = null;
let animationFrameId: number | null = null;

function afterDOMContentLoaded() {
  galleryTopRow = document.getElementById("gallery-top-scrolling-row");
  galleryBottomRow = document.getElementById("gallery-bottom-scrolling-row");

  topLoopStart = duplicateRowContentForLoop(galleryTopRow);
  bottomLoopStart = duplicateRowContentForLoop(galleryBottomRow);

  calculateAutoScrollSpeeds();
  calculateWrapDistances();

  // starting the bottom row already shifted a full loop-width in means it travels
  // toward 0 instead of away from it, so it visibly moves the opposite way to the top row
  bottomOffset = -bottomWrapDistance;
  galleryBottomRow.style.transform = `translateX(${bottomOffset}px)`;

  setupVisibilityObserver(galleryTopRow, (visible) => {
    isGalleryTopVisible = visible;
    ensureAnimationRunning();
  });
  setupVisibilityObserver(galleryBottomRow, (visible) => {
    isGalleryBottomVisible = visible;
    ensureAnimationRunning();
  });

  window.addEventListener(
    "resize",
    () => {
      calculateAutoScrollSpeeds();
      calculateWrapDistances();
    },
    { passive: true }
  );
}

// clones every item in the row once so the row is two identical, back-to-back copies:
// once the row has scrolled exactly one copy-width, resetting the offset by that same
// amount is visually seamless. Returns the first cloned item, used to measure that width.
function duplicateRowContentForLoop(row: HTMLElement): HTMLElement {
  const originalItems = Array.from(row.children);

  originalItems.forEach((item) => {
    const clone = item.cloneNode(true) as HTMLElement;
    clone.setAttribute("aria-hidden", "true");
    row.appendChild(clone);
  });

  return row.children[originalItems.length] as HTMLElement;
}

function setupVisibilityObserver(
  row: HTMLElement,
  onChange: (visible: boolean) => void
) {
  const observer = new IntersectionObserver((entries) => {
    if (entries.length > 0) {
      onChange(entries[0].isIntersecting);
    }
  });
  observer.observe(row);
}

function ensureAnimationRunning() {
  const shouldRun = isGalleryTopVisible || isGalleryBottomVisible;

  if (shouldRun && animationFrameId === null) {
    lastFrameTime = null;
    animationFrameId = requestAnimationFrame(step);
  } else if (!shouldRun && animationFrameId !== null) {
    cancelAnimationFrame(animationFrameId);
    animationFrameId = null;
  }
}

function step(timestamp: number) {
  if (lastFrameTime === null) {
    lastFrameTime = timestamp;
  }

  const deltaSeconds = (timestamp - lastFrameTime) / 1000;
  lastFrameTime = timestamp;

  if (isGalleryTopVisible && topWrapDistance > 0) {
    topOffset -= topSpeedPxPerSec * deltaSeconds;
    if (topOffset <= -topWrapDistance) {
      topOffset += topWrapDistance;
    }
    galleryTopRow.style.transform = `translateX(${topOffset}px)`;
  }

  if (isGalleryBottomVisible && bottomWrapDistance > 0) {
    bottomOffset += bottomSpeedPxPerSec * deltaSeconds;
    if (bottomOffset >= 0) {
      bottomOffset -= bottomWrapDistance;
    }
    galleryBottomRow.style.transform = `translateX(${bottomOffset}px)`;
  }

  animationFrameId = requestAnimationFrame(step);
}

function calculateAutoScrollSpeeds() {
  let speedMultiplier;

  if (window.innerWidth > 2500) {
    speedMultiplier = 0.25;
  } else if (window.innerWidth > 2000) {
    speedMultiplier = 0.5;
  } else if (window.innerWidth > mobileScreenWidth) {
    speedMultiplier = 0.75;
  } else {
    speedMultiplier = 1;
  }

  const speed = baseAutoScrollSpeed * speedMultiplier * autoScrollSpeedFactor;
  topSpeedPxPerSec = speed;
  bottomSpeedPxPerSec = speed;
}

function calculateWrapDistances() {
  topWrapDistance = topLoopStart.offsetLeft;
  bottomWrapDistance = bottomLoopStart.offsetLeft;

  // keep the running offsets in range if a breakpoint change resized the loop distance
  topOffset = topWrapDistance > 0 ? topOffset % topWrapDistance : 0;
  bottomOffset =
    bottomWrapDistance > 0 ? -(Math.abs(bottomOffset) % bottomWrapDistance) : 0;
}
